import type { CharacterPick } from "@/entities/character/ui/CharacterPickerDialog";
import type { VideoEntity } from "../api/videoService";
import type { FormState } from "./videoCreateTypes";

/**
 * 视频选人(packs W4)—— 人物库消费接线的纯函数层(可单测,UI 只调用)。
 *
 * 口径(设计 §4 消费矩阵·出镜类拍法):
 * - identity 主定妆图 objectKey → `protagonistAnchorKey`(各镜首帧 i2i 锚,W1 预留消费口;
 *   ai 侧 resolve_frame_bucket 按文件名形态归 UPLOADS 桶,pack 物料零改动即认)。
 * - appearance → 编排请求的 userInput 追加**逐字**人物卡块(文本锁:架构师照抄进
 *   storyBible.protagonist,「形象一致靠逐字 CHARACTER SHEET」老账)。
 * - 消费 v1 任务级锚(主图锚定);分镜级选锚(每镜换表情/姿态)是设计明确的 P 后续。
 */

/** 选人后写进 form 的字段(additive,草稿 formJson 往返自然携带)。 */
export interface CharacterPickFields {
  characterId?: number | null;
  characterName?: string | null;
  /** 逐字文本锁源(选人时快照;人物库后续改动不追溯已建草稿,与产品树口径一致)。 */
  characterAppearance?: string | null;
  /** identity 槽物料 objectKey 快照(主定妆图最前,≤3):提交时派生逐镜 R2V 身份参考。 */
  characterIdentityKeys?: string[] | null;
  /** 是否真人人物(0813 前置拦截判据;人物库改标记不追溯已建草稿,与 appearance 同为快照)。 */
  characterIsRealPerson?: boolean | null;
  /** 真人认证状态快照(0914 SEEDANCE P0;见 videoCreateTypes.FormState 同名字段头注)。 */
  characterVerificationStatus?: string | null;
  /** 真人素材入库状态快照(0914 契约补齐;见 videoCreateTypes.FormState 同名字段头注)。 */
  characterVerificationAssets?: string | null;
}

/** 选中人物 → form 增量(锚 + 文本锁快照 + 身份图集 + 开主角锚点)。 */
export function applyCharacterPick(form: FormState, pick: CharacterPick): FormState {
  return {
    ...form,
    characterId: pick.character.id,
    characterName: pick.character.name,
    characterAppearance: pick.character.appearance ?? null,
    characterIdentityKeys: pick.identityKeys?.length ? pick.identityKeys : null,
    characterIsRealPerson: !!pick.character.isRealPerson,
    // SEEDANCE 真人出镜 P0(0914):verification 状态与 appearance 同为选人时刻快照。
    characterVerificationStatus: pick.character.verification?.status ?? null,
    // 0914 契约补齐:assets 与 status 同批快照,两把尺一起拍(见下面判据函数头注)。
    characterVerificationAssets: pick.character.verification?.assets ?? null,
    useProtagonistAnchor: true,
    protagonistAnchorKey: pick.anchorKey,
  };
}

/** 清除选人(锚一并清;useProtagonistAnchor 回落到目的驱动的默认值由调用方决定,这里保守关掉)。 */
export function clearCharacterPick(form: FormState): FormState {
  return {
    ...form,
    characterId: null,
    characterName: null,
    characterAppearance: null,
    characterIdentityKeys: null,
    characterIsRealPerson: null,
    characterVerificationStatus: null,
    characterVerificationAssets: null,
    useProtagonistAnchor: false,
    protagonistAnchorKey: null,
  };
}

/** 判据入口两道闸共用:真人可放行 ⟺ verification.status ∈ {not_required, verified}。 */
const VERIFICATION_READY = new Set(["not_required", "verified"]);

/**
 * ★0914 契约补齐(原偏离②已裁决):两把尺,不是一把。
 *
 * `status` 答「这个人认证过没有」(P0 exempt 档下恒 `not_required`,白过);`assets` 答
 * 「这个人的定妆图/视频有没有真的入库到 vapeur、能不能送进真人线」——**P0 下真正卡门槛的
 * 是这把尺**,status 那把在 P0 基本不拦人。两把都要放行(`status` 先过,再看 `assets`)
 * 才算「这个人现在可以真人出镜」。undefined 的 assets(老后端兼容态,见 DTO 头注)按
 * `"none"` 处理 —— 未入库,不当「免检」。
 */
const ASSETS_READY = "active";

/** assets 未就绪时的人话理由(§2.4 错误码同款文案,picker 置灰与提交拦截共用一份)。 */
function assetsBlockReason(assets: string | null | undefined): string {
  const a = assets ?? "none";
  if (a === "rejected") return "素材涉及版权或公众人物，无法生成";
  if (a === "failed") return "素材准备失败";
  return "人物素材准备中，稍后再试"; // none | registering
}

/**
 * 真人前置拦截(0813 婷婷报障起 → 0914 SEEDANCE 真人出镜 P0 车 D3 改判据 → 0914 追加 assets 判据)。
 *
 * <p>★判据改版(0914):此前钉在「这个人的照片会不会真的作为参考图送进引擎」(有定妆图就拦,
 * 因为老视频模型必被肖像合规拒);**现在真人出镜走 vapeur 通道单独送真人线**(见 api A4
 * 提交守卫 + ai vapeur_seedance provider),有照片不再天然等于必失败 —— 真正决定能不能提交的
 * 是服务端认证状态机:`verification.status ∈ {not_required, verified}` 才放行。P0 exempt
 * 档下每个真人人物的 status 恒 `not_required`(见 api `RealPersonVerificationService.ensure`),
 * 所以这道闸在 P0 下光看 status **默认不拦**——只有 verification 缺席(老后端兼容态,见 DTO
 * 头注)或明确 `pending`/`failed` 才拦。
 *
 * <p>★0914 追加:status 放行后还要叠 `assets`(见 {@link ASSETS_READY} 头注「两把尺」)——
 * P0 下真正卡门槛的是这把,不是 status。素材未入库/入库中/被拒/失败都拦,文案对齐 §2.4。
 *
 * <p>此闸是**提交前**这一层;api A4 在 CreditPoolGate 之前还有一道服务端硬闸(素材未入库/被拒
 * 时报 `VENDOR_ASSET_NOT_READY`/`VENDOR_ASSET_REJECTED`)—— 两层纵深防线,这道闸挡不住的
 * 素材侧失败,提交后端的错误码人话兜底(§2.4)。
 *
 * @returns 拦截理由(人话,可直接进 submitError / 面板提示);不该拦返回 null。
 */
export function realPersonBlockReason(form: FormState): string | null {
  if (!form.characterId || !form.characterIsRealPerson) return null;
  const who = (form.characterName ?? "").trim() || "所选人物";
  if (!VERIFICATION_READY.has(form.characterVerificationStatus ?? "")) {
    return (
      `「${who}」是真人人物,当前认证状态还不能用于出镜(${
        form.characterVerificationStatus
          ? `状态:${form.characterVerificationStatus}`
          : "未拿到认证信息"
      }) —— 这单提交上去会被拒。` +
      "去人物详情补完认证,或者换一位虚拟人物、清除选人让引擎自产主角。"
    );
  }
  // status 放行后叠 assets:P0 exempt 档下这才是真正的门槛(见上方「两把尺」头注)。
  if (form.characterVerificationAssets === ASSETS_READY) return null;
  return (
    `「${who}」${assetsBlockReason(form.characterVerificationAssets)} —— 这单提交上去会被拒。` +
    "换一位虚拟人物、清除选人让引擎自产主角,或稍后再试。"
  );
}

/**
 * 选人器可用性筛子(2026-08-13 用户拍板起 → 0914 SEEDANCE 真人出镜 P0 车 D3 改判据 →
 * 0914 追加 assets 判据)。
 *
 * <p>与 {@link realPersonBlockReason} **同一条判据**(status 放行后还要 assets ===
 * "active"),只是提前到列表那一刻。P0 exempt 档下真人人物的 status 恒 `not_required`,
 * 但 assets 要等 vapeur 入库 worker(车 B)真的把素材注册成 `active` 才放行 —— 不再按
 * 「有没有定妆图」筛(那是切换到 vapeur 真人线之前的老判据,见 git blame)。
 *
 * <p>★这一版**不再把「未就绪的真人」整条从列表里拿掉**:选择器(`CharacterPickerDialog`)
 * 用这个判据的结果决定置灰 + 原因文案,而不是隐藏(D4,`verification.status=pending/failed`
 * 或 `assets` 未就绪时用户应该看得见「有这个人,但现在不能选、为什么」,不是当它不存在)。
 * 这个函数本身仍是纯判据(true=可选),**是否隐藏 vs 置灰是调用方的展示决定**,不是这里管的事。
 *
 * <p>undefined 的 verification/assets(老后端兼容态,见 DTO 头注)按「未验证」/「未入库」
 * 处理 —— 宁可旧后端下真人暂不可选,也不能因为字段缺席就放行一条没有认证/素材兜底的真人出镜线。
 */
export function videoUsableCharacter(c: {
  isRealPerson?: boolean;
  verification?: { status?: string; assets?: string } | null;
}): boolean {
  if (!c.isRealPerson) return true;
  if (!VERIFICATION_READY.has(c.verification?.status ?? "")) return false;
  return c.verification?.assets === ASSETS_READY;
}

/**
 * 提交时派生逐镜身份参考(2026-08-04 拍板:选人默认路径此前只消费主定妆图 1 张,
 * R2V 多图身份锁要手工挂 —— 演示片实证的正确形态自动化)。
 *
 * - assetRef = 裸 objectKey(与 motionPick 同约定,ai 编译层现签);job 必写;
 * - 只挂「本镜确有主角」的镜:panelSubjectRefs[i] 提到人物名才挂;整表缺失 → 全挂
 *   (bare 提交没有镜表 → 原样返回,i2i 锚照旧);
 * - 该镜已有手工 identity 参考 → 不动(手选优先,不叠加);
 * - 纯派生:不落 form.references(草稿里存的始终是用户手选的那份)。
 */
export function withIdentityReferences(form: FormState): FormState["references"] {
  const keys = form.characterIdentityKeys ?? [];
  const shotCount = form.panelScenes?.length ?? 0;
  if (!form.characterId || !keys.length || !shotCount) return form.references;
  const name = (form.characterName ?? "").trim();
  const subjectRefs = form.panelSubjectRefs ?? [];
  const identityRefs = keys.map((key) => ({
    assetRef: key,
    role: "identity" as const,
    job: "出镜人即此人",
  }));
  return Array.from({ length: shotCount }, (_, i) => {
    const own = form.references?.[i] ?? [];
    if (own.some((r) => r.role === "identity")) return own;
    const subject = subjectRefs[i];
    const featured = !subjectRefs.length || !name || (subject ?? "").includes(name);
    return featured ? [...identityRefs, ...own] : own;
  });
}

/**
 * 编排 userInput 追加逐字人物卡块。**提交时拼,不污染输入框**(用户看到的还是自己写的一句话)。
 * appearance 为空(人物没填外观)→ 只带名字提示,不造假描述。
 */
export function withCharacterSheet(
  userInput: string,
  fields: Pick<CharacterPickFields, "characterName" | "characterAppearance">
): string {
  const name = fields.characterName?.trim();
  if (!name) return userInput;
  const appearance = fields.characterAppearance?.trim();
  const sheet = appearance
    ? `主角人物「${name}」,外观逐字保持:${appearance}`
    : `主角人物「${name}」(以主角参考图为准)`;
  return `${userInput.trim()}\n【主角人物卡】${sheet}`;
}

/**
 * 编排重置时锚字段的保留策略:选了库人物 → 锚是用户手选,编排不得清
 * (与 lockedFields「用户手选优先」同一原则);没选 → 维持既有行为(清空,由出帧回填)。
 */
export function composeResetAnchor(form: FormState): Pick<FormState, "protagonistAnchorKey"> {
  return { protagonistAnchorKey: form.characterId ? form.protagonistAnchorKey : null };
}

/**
 * 出帧轮询回填时锚字段的合并策略:选了库人物 → 本地锚(人物主定妆图)优先,服务端自产
 * 定妆图不覆盖;没选 → 服务端值优先(今天的行为)。
 */
export function mergeAnchorFromServer(
  form: FormState,
  serverKey: string | null | undefined
): string | null | undefined {
  if (form.characterId) return form.protagonistAnchorKey ?? serverKey;
  return serverKey ?? form.protagonistAnchorKey;
}

/** 选人实体的稳定 id 前缀(重复注入判据;LIB=library, C=character)。 */
const LIB_CHARACTER_ID = (characterId: number) => `LIBC${characterId}`;

/**
 * W0 事故修(2026-08-03,任务 4853229421568):选人此前只进文本卡,主定妆图从未上车 →
 * Seedance 渲染端 entities:null,成片人物与选择无关。此函数在 payload 出口把选中人物
 * 注入 entities 实体表(role:"character" + 定妆图 key),与上传产品图共用同一载具,
 * ai 侧 R2V 身份锁(chunk_orchestration._entity_refs)按 role 消费。
 * 出口注入而非落 form:ProductImagePanel 的 onChange 会整表覆盖 entities,
 * 落 form 会被产品面板一次交互冲掉。
 */
export function entitiesWithCharacter(form: FormState): FormState["entities"] {
  const key = form.characterId ? form.protagonistAnchorKey : null;
  if (!form.characterId || !key) return form.entities;
  const entity: VideoEntity = {
    id: LIB_CHARACTER_ID(form.characterId),
    role: "character",
    source: "image",
    key,
    productId: "",
    name: form.characterName ?? "",
    confidence: 1,
  };
  const rest = (form.entities ?? []).filter((e) => e.id !== entity.id);
  return [...rest, entity];
}

/**
 * 草稿回载时剥离注入的选人实体:它是 characterId 的派生物,每次出口(存草稿/建任务)重新注入。
 * 留在 form 里会被 ProductImagePanel 当上传图展示,且面板 onChange 整表覆盖时一并冲掉。
 */
export function stripLibCharacterEntities(entities: FormState["entities"]): FormState["entities"] {
  if (!entities?.length) return entities;
  const rest = entities.filter((e) => !e.id.startsWith("LIBC"));
  return rest.length === entities.length ? entities : rest;
}
